// Press-and-hold anywhere on the floating timer bar moves it (James, 2026-10-02).
//
// The bar (pages/TimerBar.js) is a tiny always-on-top desktop window.  Its
// background, clock and matter name drag natively (-webkit-app-region: drag),
// but a native drag region swallows clicks on Windows, so the BUTTONS cannot be
// drag regions.  With no timer open the bar is one big "⏱ Start a timer"
// button, so the only thing left to grab was the thin dark rim around it.
//
// This decides, for a press on a button, whether it is a CLICK or a DRAG:
//   - a press that travels no more than DRAG_THRESHOLD_PX is an ordinary click;
//   - once it travels further it becomes a drag — start() once, then move() on
//     every pointer move, then end() when the button is released;
//   - the click Chromium dispatches after a drag is swallowed, so dropping the
//     bar never starts or pauses a timer by accident.
//
// It does NOT move the window.  start/move/end go to the desktop app
// (electronAPI.timerBar.dragStart/dragMove/dragEnd, desktop 1.0.14+), which
// follows the real mouse itself.  window.moveTo from the page cannot do it:
// Chromium clamps a page-requested window to a 100×100 minimum and to the
// screen it is already on, so the 48px bar would grow to 100px and could never
// reach a second monitor — proved under Electron 28.3.3.  See
// desktop/timerBar.js beginBarDrag.
//
// MOUSE ONLY.  The desktop app follows the OS cursor (getCursorScreenPoint),
// and on Windows a finger or pen on the screen does not move that cursor —
// Chromium consumes touch WM_POINTER messages itself — so a touch "drag" would
// leave the bar behind and drop it relative to wherever the mouse was last
// parked, possibly on another monitor.  Touch also has a far larger tap slop
// (Chromium accepts a finger that wanders 15 DIP, a pen 20) than this 4px
// threshold, so treating touch as a drag would eat ordinary taps on ⏸.  A
// touch or pen press is therefore an ordinary tap, exactly as on desktop
// 1.0.13 (review of the 1.0.14 candidate, 2026-10-03).
//
// Framework-free so it can be unit-tested and driven by a real Electron window
// in a probe; hooks/useWindowDrag.js wraps it for React.

export const DRAG_THRESHOLD_PX = 4
// Long enough to cover the click Chromium dispatches right after pointerup,
// short enough that a deliberate click a moment later is never eaten.
export const SUPPRESS_CLICK_MS = 400

const finite = (n) => typeof n === 'number' && Number.isFinite(n)

export function passedThreshold (start, screenX, screenY, threshold = DRAG_THRESHOLD_PX) {
  return Math.abs(screenX - start.screenX) > threshold || Math.abs(screenY - start.screenY) > threshold
}

// start(dx, dy) / move() / end(): tell whoever moves the window.  dx/dy is
// how far the pointer travelled between the press and the drag being
// recognised (screen px), so the spot that was pressed can stay under the
// mouse.  now(): a millisecond clock.
export function createWindowDrag ({ start, move, end, now, threshold = DRAG_THRESHOLD_PX } = {}) {
  let drag = null
  let suppressUntil = 0

  const sameDrag = (e) => drag && e && e.pointerId === drag.pointerId
  const call = (fn, ...args) => { try { if (typeof fn === 'function') fn(...args) } catch {} }

  return {
    // Returns true when this press is being tracked.  Only the primary button
    // of the primary MOUSE pointer: a right-click opens the bar's menu, and a
    // touch or pen press stays a plain tap (see the header).
    down (e) {
      if (!e || e.pointerType !== 'mouse' || e.button !== 0 || e.isPrimary === false) return false
      if (!finite(e.screenX) || !finite(e.screenY)) return false
      // A press that somehow never saw its release (focus stolen mid-press)
      // must not leave the window following the mouse.
      if (drag && drag.moved) call(end)
      drag = { pointerId: e.pointerId, screenX: e.screenX, screenY: e.screenY, moved: false }
      return true
    },

    // 'idle'     — not our press (or no press at all)
    // 'pending'  — pressed, not far enough yet: still a click
    // 'started'  — just became a drag
    // 'dragging' — following the mouse
    move (e) {
      if (!sameDrag(e) || !finite(e.screenX) || !finite(e.screenY)) return 'idle'
      // The left button is no longer down: its release went somewhere else (a
      // native menu or dialog took it — on a Mac, ctrl+click on a button is a
      // left press that opens the bar's menu).  Without this, the next plain
      // hover would become a drag and the bar would stick to the pointer.  No
      // click follows a release we never saw, so nothing is swallowed.
      if (typeof e.buttons === 'number' && (e.buttons & 1) === 0) {
        const d = drag
        drag = null
        if (d.moved) call(end)
        return 'idle'
      }
      if (!drag.moved) {
        if (!passedThreshold(drag, e.screenX, e.screenY, threshold)) return 'pending'
        drag.moved = true
        call(start, e.screenX - drag.screenX, e.screenY - drag.screenY)
        call(move)
        return 'started'
      }
      call(move)
      return 'dragging'
    },

    // Returns true when the press had become a drag; the click that follows
    // is swallowed.
    up (e) {
      if (!sameDrag(e)) return false
      const d = drag
      drag = null
      if (!d.moved) return false
      call(end)
      suppressUntil = now() + SUPPRESS_CLICK_MS
      return true
    },

    // The OS or the browser took the pointer away (pointercancel, lost
    // capture).  The window stays where it already is.
    cancel (e) {
      if (!sameDrag(e)) return
      if (drag.moved) {
        call(end)
        suppressUntil = now() + SUPPRESS_CLICK_MS
      }
      drag = null
    },

    // The press is being abandoned on purpose (the bar's right-click menu is
    // about to open while the left button is held).  Like cancel(), but for
    // whatever press is tracked: a press that was still a click ends silently;
    // a drag ends AND its trailing click is swallowed, exactly as cancel()
    // does — dismiss the menu quickly and release, and Chromium still clicks
    // the button the bar was being dragged by (verification of the 1.0.14
    // fixes, chorded right-click mid-drag).
    cancelPress () {
      if (!drag) return
      const d = drag
      drag = null
      if (d.moved) {
        call(end)
        suppressUntil = now() + SUPPRESS_CLICK_MS
      }
    },

    // Called for every click.  True = this click ended a drag: swallow it.
    // One-shot, and never for keyboard activation (detail 0), so Enter/Space
    // on a focused button always works.
    consumeClick (e) {
      if (e && e.detail === 0) return false
      if (suppressUntil && now() < suppressUntil) { suppressUntil = 0; return true }
      suppressUntil = 0
      return false
    },

    isDragging () { return !!(drag && drag.moved) }
  }
}
